import { Typography, Link, styled, Box } from '@mui/material'
import React from 'react'
import { InfoIcon } from '../../Icons/Icons'
import {
  SectionRoot,
  ContainerInformation,
  TitleText,
  CardContainer,
} from './VenueCardContainerStyles'
import { Actions } from '../../../Views/EventPage/EventPage'

interface CardRootProps {
  compact: boolean
  noBottomMargin?: boolean
  isEmpty?: boolean
}
interface EmptyLinkProps {
  emptyText?: boolean
}

const compactRootProps = {
  display: 'inline-block',
  marginLeft: 0,
  marginRight: 0,
  verticalAlign: 'top',
  width: 'calc(50% - 8px)',
  '&.compact ~ .compact': { marginLeft: 16 },
}

const CardRoot = styled(SectionRoot, {
  shouldForwardProp: (propName) =>
    propName !== 'compact' && propName !== 'noBottomMargin',
})<CardRootProps>(({ isEmpty, theme, compact, noBottomMargin }) => ({
  padding: isEmpty ? '1rem 0' : '1rem',
  marginBottom: noBottomMargin ? 0 : '2rem',
  borderRadius: noBottomMargin ? '8px 8px 0 0' : '8px',
  [theme.breakpoints.up('sm')]: {
    padding: '1rem',
  },
  [theme.breakpoints.up('md')]: {
    ...(compact && compactRootProps),
  },
}))

interface VenueCardContainerProps {
  title: React.ReactNode
  subtitle?: React.ReactNode
  contentHeader?: React.ReactNode
  banner?: React.ReactNode
  emptyState?: React.ReactNode
  compact?: boolean
  noBottomMargin?: boolean
  status?: string | null
  children?: JSX.Element[]
  actions?: Actions
}

const VenueCardContainer = ({
  title,
  subtitle,
  banner,
  compact = false,
  emptyState,
  status,
  actions,
  children,
  contentHeader,
  noBottomMargin,
}: VenueCardContainerProps): JSX.Element => {
  const showStopRecievingMessages = status && true
  const hasChildren = !!React.Children.count(children) && children !== undefined
  const { openStopMessages } = actions ?? { openStopMessages: () => {} }

  const StopEmailLink = styled('div', {
    shouldForwardProp: (p) => p !== 'emptyText',
  })<EmptyLinkProps>(({ emptyText, theme }) => ({
    margin: '1.5rem 0 0.5rem',
    marginLeft: emptyText ? '1rem' : '0',
    fontWeight: 500,
    [theme.breakpoints.up('sm')]: {
      marginLeft: 0,
    },
  }))

  return (
    <CardRoot
      noBottomMargin={noBottomMargin}
      isEmpty={!hasChildren}
      compact={compact}
      className={`card-root${compact ? ' compact' : ''}`}
    >
      <ContainerInformation isEmpty={!hasChildren}>
        <TitleText variant='h2' order={1}>
          {title}
        </TitleText>
        {hasChildren && subtitle && (
          <Typography className='subtitle' minWidth='80%' order={2}>
            {subtitle}
          </Typography>
        )}
      </ContainerInformation>
      {hasChildren ? (
        <>
          {banner && banner}
          {contentHeader && contentHeader}
          <CardContainer>{children}</CardContainer>
        </>
      ) : (
        emptyState
      )}
      {showStopRecievingMessages &&
        (status === 'closed' ? (
          <Box
            display='flex'
            flexDirection='row'
            gap='8px'
            marginTop='24px'
            marginBottom='8px'
          >
            <InfoIcon />
            <Typography fontWeight={500}>
              You told us you don’t want to receive messages from any more
              venues.
            </Typography>
          </Box>
        ) : (
          <StopEmailLink emptyText={!hasChildren}>
            <Link
              variant='anchor'
              underline='none'
              onClick={() => openStopMessages()}
              component='button'
            >
              Stop receiving messages
            </Link>
          </StopEmailLink>
        ))}
    </CardRoot>
  )
}

export default VenueCardContainer
